import type { Role } from "./api";

// Backend jargon that must never reach a non-dev operator. If a raw error
// message contains any of these tokens, we replace it with plain-English copy.
// Keep this list lowercase; matching is case-insensitive.
const JARGON = [
  "encoder",
  "encode",
  "nvenc",
  "vbr",
  "cbr",
  "bitrate",
  "gpu",
  "cuda",
  "mux",
  "nvstreammux",
  "nvinfer",
  "deepstream",
  "qdrant",
  "minio",
  "redis",
  "codec",
  "h.264",
  "h264",
  "h.265",
  "h265",
  "hevc",
  "rtsp",
  "webrtc",
  "hls",
  "tensorrt",
  "pipeline",
  "lease",
  "transcode",
  "passthrough",
  "keyframe",
  "gop",
  "vbv",
  "tmpfs",
  "consumer group",
  "traceback",
];

// Ordered pattern -> friendly message. First match wins. Patterns are matched
// against the lowercased raw message.
const FRIENDLY_RULES: Array<{ test: RegExp; message: string }> = [
  // Recorded-playback failures come back as copy already written for
  // operators, but the backend knows things the generic rules below would
  // throw away — "no footage for that time" is a recording-schedule problem
  // the operator can fix, and it must not degrade to "something went wrong".
  {
    test: /live video is off/,
    message: "Live video is off for this store. Turn it on in Settings.",
  },
  {
    test: /no gpu worker is online/,
    message:
      "No video processing server is online. Live video and detection start again as soon as one connects.",
  },
  {
    test: /before this simulation started/,
    message:
      "That moment is before this simulation started, so there is no recording to play.",
  },
  {
    test: /no footage|not set to record|recording schedule/,
    message:
      "There is no recording for this camera at that time. Check that the channel is set to record on the recorder.",
  },
  {
    test: /not backed by an nvr|requires an nvr channel/,
    message:
      "This camera has no recorder behind it, so there is no past footage to play.",
  },
  {
    test: /simulator|simulated camera/,
    message:
      "This simulated camera's recording could not be found. The simulator may still be starting up.",
  },
  {
    test: /rejected the stored nvr credentials|nvr credentials/,
    message: "The recorder rejected the saved sign-in details for this camera.",
  },
  {
    test: /recorder could not be reached|recorder did not return/,
    message:
      "The recorder could not be reached, or took too long to answer. Try a shorter clip, or check the recorder is online.",
  },
  {
    test: /failed to transcode|could not seek|clip source file/,
    message: "The recording could not be prepared for playback.",
  },
  {
    test: /simulation clip finished|simulation has finished/,
    message: "This simulation has finished.",
  },
  {
    test: /mux pad|max_batch_size|batch|ai source|capacity slot/,
    message:
      "The maximum number of AI cameras is already in use. Remove an AI camera, or set this one to Non-AI, to free a slot.",
  },
  {
    test: /encoder|nvenc|at max|capacity|concurrent|pool|limit reached/,
    message:
      "The system is handling as many live views as it can right now. Try again in a moment, or switch this camera to the Original stream.",
  },
  {
    test: /not streaming|no frame|no video|source|camera .* (down|dead|degraded|reconnect)/,
    message: "This camera isn't sending video right now. It may be reconnecting.",
  },
  {
    test: /unreachable|did not answer|timed out|timeout|busy|unavailable|502|503|504/,
    message: "The video service is temporarily unavailable. Please try again shortly.",
  },
  {
    test: /unknown quality|preset|wall profile|out of range|resolution|quality/,
    message: "That quality option isn't available for this camera.",
  },
  {
    test: /unknown preview mode|mode/,
    message: "That view isn't available for this camera.",
  },
];

const GENERIC_FALLBACK = "Something went wrong. Please try again.";

function rawMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message ?? "");
  }
  return "";
}

/** True when a message leaks internal/technical vocabulary. */
export function containsJargon(message: string): boolean {
  const m = message.toLowerCase();
  return JARGON.some((tok) => m.includes(tok));
}

/**
 * Role-aware error copy. Developers see the raw backend cause verbatim (words
 * like "encoder" / "vbr" are useful to them). Everyone else gets plain-English
 * copy: a mapped friendly message when we recognise the failure, otherwise a
 * generic fallback — and we never surface a message that contains jargon.
 */
export function friendlyError(err: unknown, role: Role, fallback = GENERIC_FALLBACK): string {
  const raw = rawMessage(err).trim();
  if (role === "dev") return raw || fallback;
  if (!raw) return fallback;

  const lower = raw.toLowerCase();
  for (const rule of FRIENDLY_RULES) {
    if (rule.test.test(lower)) return rule.message;
  }
  // Unrecognised but clean enough to show (no jargon, short) -> show as-is.
  // The cap is two sentences' worth: backend copy written for operators
  // routinely says what went wrong AND what to do about it, and cutting that
  // to a generic apology loses the only actionable half.
  if (!containsJargon(raw) && raw.length <= 220) return raw;
  return fallback;
}
